import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [modelsDropdownOpen, setModelsDropdownOpen] = useState(false);
  const [mobileModelsExpanded, setMobileModelsExpanded] = useState(false);
  const dropdownRef = useRef(null);
  const { isAuthenticated, logout } = useAuth();

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    setMobileModelsExpanded(false); // Reset mobile models dropdown when closing mobile menu
  };

  const toggleMobileModels = () => {
    setMobileModelsExpanded(!mobileModelsExpanded);
  };


  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setModelsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
      <div className="max-w-[1600px] mx-auto px-6 sm:px-8 lg:px-12 h-16 flex items-center">
        {/* Logo - left aligned on both mobile and desktop */}
        <div className="flex items-center justify-start flex-grow md:flex-grow-0">
          <Link to="/" className="flex items-center mx-0">
            <div className="flex items-center">
              <img 
                src="/fetch-logo.svg" 
                alt="fetch.ai logo" 
                className="h-6" 
              />
            </div>
          </Link>
        </div>
        
        {/* Mobile burger menu button - only visible on mobile, positioned on right */}
        <div className="md:hidden ml-auto">
          <button 
            className="text-gray-700 p-2 focus:outline-none"
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              className="h-6 w-6" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M4 6h16M4 12h16M4 18h16" 
              />
            </svg>
          </button>
        </div>

        {/* Empty flex space to push items right - hidden on mobile */}
        <div className="hidden md:block md:flex-1"></div>

        {/* Desktop Navigation - hidden on mobile */}
        <div className="hidden md:flex items-center space-x-6">
          <Link 
            to="/" 
            className="text-sm text-gray-800 font-medium hover:text-blue-600"
          >
            Home
          </Link>
          <Link 
            to="/benchmarks" 
            className="text-sm text-gray-800 font-medium hover:text-blue-600"
          >
            Benchmarks
          </Link>
          <Link
            to="/status"
            className="text-sm text-gray-800 font-medium hover:text-blue-600"
          >
            Status
          </Link>
          <Link
            to="/monitor"
            className="text-sm text-gray-800 font-medium hover:text-blue-600"
          >
            Monitor
          </Link>
          <a
            href="https://api-docs.us.inc/#c86e19e2-ffeb-4742-a3a3-4fa69e9cfedd"
            target="_blank" 
            rel="noopener noreferrer"
            className="text-sm text-gray-800 font-medium hover:text-blue-600"
          >
            API Docs
          </a>
          
          {/* Models Dropdown */}
          <div 
            className="relative"
            ref={dropdownRef}
            onMouseEnter={() => setModelsDropdownOpen(true)}
            onMouseLeave={() => setModelsDropdownOpen(false)}
          >
            <button
              className="flex items-center text-sm text-gray-800 font-medium hover:text-blue-600 focus:outline-none"
              onClick={() => setModelsDropdownOpen(!modelsDropdownOpen)}
              aria-expanded={modelsDropdownOpen}
              aria-haspopup="true"
            >
              Models
              <svg 
                className={`ml-1 h-4 w-4 transition-transform duration-200 ${modelsDropdownOpen ? 'rotate-180' : ''}`}
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {/* Dropdown Menu */}
            <div className={`absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50 transform transition-all duration-200 ${
              modelsDropdownOpen 
                ? 'opacity-100 translate-y-0 visible' 
                : 'opacity-0 -translate-y-2 invisible'
            }`}>
              <Link
                to="/voice-model"
                className="block px-4 py-2 text-sm text-gray-800 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                onClick={() => setModelsDropdownOpen(false)}
              >
                ASI-1 Voice Model
              </Link>
              <Link
                to="/mini-model"
                className="block px-4 py-2 text-sm text-gray-800 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                onClick={() => setModelsDropdownOpen(false)}
              >
                ASI1 Mini
              </Link>
              <Link
                to="/fast-model"
                className="block px-4 py-2 text-sm text-gray-800 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                onClick={() => setModelsDropdownOpen(false)}
              >
                ASI1 Fast
              </Link>
              <Link
                to="/graph-model"
                className="block px-4 py-2 text-sm text-gray-800 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                onClick={() => setModelsDropdownOpen(false)}
              >
                ASI Graph
              </Link>
              <Link
                to="/extended-model"
                className="block px-4 py-2 text-sm text-gray-800 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                onClick={() => setModelsDropdownOpen(false)}
              >
                ASI-1 Mini-Extended
              </Link>
            </div>
          </div>

          
          <a 
            href="https://docs.asi1.ai/docs/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-sm text-gray-800 font-medium hover:text-blue-600"
          >
            ASI Documentation
          </a>
          <Link 
            to="/dynamic-agentic-system"
            className="text-sm text-gray-800 font-medium hover:text-blue-600"
          >
            Dynamic Agentic System
          </Link>
          <a 
            href="https://asi1.ai/"
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors ml-2"
          >
            Platform
          </a>
          {!isAuthenticated ? (
            <Link
              to="/login"
              className="px-4 py-2 border border-blue-600 text-blue-600 text-sm font-medium rounded-lg hover:bg-blue-50 transition-colors"
            >
              Login
            </Link>
          ) : (
            <button
              onClick={logout}
              className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-100 transition-colors"
            >
              Logout
            </button>
          )}
        </div>

        {/* Mobile menu - slide-out panel */}
        <div
          className={`fixed inset-0 z-50 md:hidden transition-opacity duration-200 ${mobileMenuOpen ? 'visible opacity-100' : 'invisible opacity-0'}`}
        >
          <div className="absolute inset-0 bg-black/50" onClick={toggleMobileMenu} />
          <div className="relative h-full bg-white flex flex-col items-center justify-center px-6 py-10 space-y-6 text-center overflow-y-auto">
            <button
              className="absolute top-4 right-4 text-gray-600"
              onClick={toggleMobileMenu}
              aria-label="Close menu"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <nav className="w-full max-w-xs space-y-5">
              <Link to="/" className="block text-lg font-medium text-gray-800 hover:text-blue-600" onClick={toggleMobileMenu}>Home</Link>
              <Link to="/benchmarks" className="block text-lg font-medium text-gray-800 hover:text-blue-600" onClick={toggleMobileMenu}>Benchmarks</Link>
              <Link to="/status" className="block text-lg font-medium text-gray-800 hover:text-blue-600" onClick={toggleMobileMenu}>Status</Link>
              <Link to="/monitor" className="block text-lg font-medium text-gray-800 hover:text-blue-600" onClick={toggleMobileMenu}>Monitor</Link>
              <a
                href="https://api-docs.us.inc/#c86e19e2-ffeb-4742-a3a3-4fa69e9cfedd"
                target="_blank"
                rel="noopener noreferrer"
                className="block text-lg font-medium text-gray-800 hover:text-blue-600"
                onClick={toggleMobileMenu}
              >
                API Docs
              </a>

              <div className="border-t border-gray-200 pt-4 mt-4">
                <button
                  className="flex items-center justify-center gap-2 w-full text-lg font-medium text-gray-800 hover:text-blue-600"
                  onClick={toggleMobileModels}
                  aria-expanded={mobileModelsExpanded}
                >
                  Models
                  <svg
                    className={`h-4 w-4 transition-transform duration-200 ${mobileModelsExpanded ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div
                  className={`mt-3 space-y-3 transition-all duration-200 ${
                    mobileModelsExpanded ? 'opacity-100 max-h-96' : 'opacity-0 max-h-0 overflow-hidden'
                  }`}
                >
                  <Link to="/voice-model" className="block text-sm font-medium text-gray-700 hover:text-blue-600" onClick={toggleMobileMenu}>ASI-1 Voice Model</Link>
                  <Link to="/mini-model" className="block text-sm font-medium text-gray-700 hover:text-blue-600" onClick={toggleMobileMenu}>ASI1 Mini</Link>
                  <Link to="/fast-model" className="block text-sm font-medium text-gray-700 hover:text-blue-600" onClick={toggleMobileMenu}>ASI1 Fast</Link>
                  <Link to="/graph-model" className="block text-sm font-medium text-gray-700 hover:text-blue-600" onClick={toggleMobileMenu}>ASI Graph</Link>
                  <Link to="/extended-model" className="block text-sm font-medium text-gray-700 hover:text-blue-600" onClick={toggleMobileMenu}>ASI-1 Mini-Extended</Link>
                  <Link to="/finance-expert-agent" className="block text-sm font-medium text-gray-700 hover:text-blue-600" onClick={toggleMobileMenu}>ASI Finance Expert Agent</Link>
                </div>
              </div>

              <a
                href="https://docs.asi1.ai/docs/"
                target="_blank"
                rel="noopener noreferrer"
                className="block text-lg font-medium text-gray-800 hover:text-blue-600"
                onClick={toggleMobileMenu}
              >
                ASI Documentation
              </a>
              <Link to="/dynamic-agentic-system" className="block text-lg font-medium text-gray-800 hover:text-blue-600" onClick={toggleMobileMenu}>Dynamic Agentic System</Link>
              <a
                href="https://asi1.ai/"
                target="_blank"
                rel="noopener noreferrer"
                className="block text-lg font-medium text-white bg-blue-600 px-4 py-2 rounded-lg hover:bg-blue-700"
                onClick={toggleMobileMenu}
              >
                Platform
              </a>

              {!isAuthenticated ? (
                <Link
                  to="/login"
                  className="block text-lg font-medium text-blue-600 border border-blue-600 px-4 py-2 rounded-lg"
                  onClick={toggleMobileMenu}
                >
                  Login
                </Link>
              ) : (
                <button
                  className="block w-full text-lg font-medium text-gray-700 border border-gray-300 px-4 py-2 rounded-lg"
                  onClick={() => {
                    logout();
                    toggleMobileMenu();
                  }}
                >
                  Logout
                </button>
              )}
            </nav>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
